// src/lib/format.ts
// 表示用フォーマッタ群

/** "YYYY-MM-DD" / "YYYY-MM" → "YY年MM月" (ゼロ埋め・年2桁)。
 *  サイドバー選択UI / ヘッダ・条件バーの対象月 pill / 各パネル pill で統一する年月表記ルール。 */
export const ymJp = (d: string | null | undefined): string =>
  !d || d.length < 7 ? (d ?? "") : `${d.slice(2, 4)}年${d.slice(5, 7)}月`;

export const fmtInt = (x: number | null | undefined): string =>
  x == null || !Number.isFinite(x) ? "—" : Math.round(x).toLocaleString();

export const fmt1   = (x: number | null | undefined): string =>
  x == null || !Number.isFinite(x) ? "—" : x.toLocaleString(undefined, { maximumFractionDigits: 1 });

export const fmt2   = (x: number | null | undefined): string =>
  x == null || !Number.isFinite(x) ? "—" : x.toLocaleString(undefined, { maximumFractionDigits: 2 });

/**
 * 千円単位の数値を 千円 表記でそのまま桁区切り
 * 例: 1469206 → "1,469,206 千円"
 */
export const fmtYenK = (x: number | null | undefined): string => {
  if (x == null || !Number.isFinite(x)) return "—";
  return `${Math.round(x).toLocaleString()} 千円`;
};

/** 円単位の数値を 千円 / 百万円 / 億円 に変換 */
export const fmtYen = (x: number | null | undefined): string => {
  if (x == null || !Number.isFinite(x)) return "—";
  const abs = Math.abs(x);
  if (abs >= 1e8) return `${(x / 1e8).toFixed(1)} 億円`;
  if (abs >= 1e6) return `${(x / 1e6).toFixed(0)} 百万円`;
  if (abs >= 1e3) return `${(x / 1e3).toFixed(0)} 千円`;
  return `${Math.round(x).toLocaleString()} 円`;
};

export const fmtPct = (x: number | null | undefined, digits = 1): string =>
  x == null || !Number.isFinite(x) ? "—" : `${(x * 100).toFixed(digits)} %`;

/** 達成率 (current / budget) を %, 符号付き で */
export const fmtRatio = (x: number | null | undefined, digits = 0): string =>
  x == null || !Number.isFinite(x) ? "—" : `${(x * 100).toFixed(digits)}%`;

/** YoY/MoM 変化率 を 符号付き % で (例: 1.112/1.000 → +11.2%) */
export const fmtDelta = (cur: number | null | undefined, ref: number | null | undefined): string => {
  if (cur == null || ref == null || !Number.isFinite(cur) || !Number.isFinite(ref) || ref === 0) return "—";
  const delta = (cur - ref) / Math.abs(ref);
  const sign = delta > 0 ? "+" : "";
  return `${sign}${(delta * 100).toFixed(1)}%`;
};

/** YoY/MoM 比率 (cur/ref) を 比率 % で (例: 1.112/1.000 → 111.2%) */
export const fmtRatioPct = (cur: number | null | undefined, ref: number | null | undefined): string => {
  if (cur == null || ref == null || !Number.isFinite(cur) || !Number.isFinite(ref) || ref === 0) return "—";
  return `${(cur / ref * 100).toFixed(1)}%`;
};

/** 集計区分の値を unit に合わせて表示 (単位サフィックスなし、 値のみ)
 *   行/列に単位列が別途あるテーブル用 (MetricMatrix 等)。
 *   例: 1469206, "千円" → "1,469,206" (" 千円" を付けない) */
export function fmtByUnitValueOnly(value: number | null | undefined, unit: string): string {
  if (value == null || !Number.isFinite(value)) return "—";
  switch (unit) {
    case "千円":  return Math.round(value).toLocaleString();
    case "円":    return Math.round(value).toLocaleString();
    case "%":     return (value * 100).toFixed(1);
    case "円/日": return Math.round(value).toLocaleString();
    case "日":    return fmt1(value);
    case "人":    return fmt1(value);
    case "人日":  return fmtInt(value);
    case "件":    return fmtInt(value);
    default:      return fmtInt(value);
  }
}

/** 集計区分の値を unit に合わせて表示 */
export function fmtByUnit(value: number | null | undefined, unit: string): string {
  if (value == null || !Number.isFinite(value)) return "—";
  switch (unit) {
    case "千円": return fmtYenK(value);
    case "円":   return fmtYen(value);
    case "%":    return fmtPct(value);
    case "円/日": return `${Math.round(value).toLocaleString()} 円`;
    case "日":   return fmt1(value);
    case "人":   return fmt1(value);
    case "人日": return fmtInt(value);
    case "件":   return fmtInt(value);
    default:     return fmtInt(value);
  }
}

/** 集計区分の "小さい/大きい" 単位を返す (KPIカードのメイン値表示用) */
export function fmtByUnitMain(value: number | null | undefined, unit: string): { value: string; unit: string } {
  if (value == null || !Number.isFinite(value)) return { value: "—", unit };
  switch (unit) {
    case "千円":
      // 入院収入などは 千円 単位そのままで表示 (1,469,206 千円)
      return { value: Math.round(value).toLocaleString(), unit: "千円" };
    case "円": {
      // 億円 (≥1億) / 万円 (≥1万) に自動スケール (2026-06-24、 カードの円表示用)。
      const abs = Math.abs(value);
      if (abs >= 1e8) return { value: (value / 1e8).toFixed(2), unit: "億円" };
      if (abs >= 1e4) return { value: Math.round(value / 1e4).toLocaleString(), unit: "万円" };
      return { value: Math.round(value).toLocaleString(), unit: "円" };
    }
    case "%":    return { value: (value * 100).toFixed(1), unit: "%" };
    case "円/日": return { value: Math.round(value).toLocaleString(), unit: "円/日" };
    case "日":   return { value: value.toFixed(1), unit: "日" };
    case "人":   return { value: value.toFixed(1), unit: "人" };
    case "人日": return { value: Math.round(value).toLocaleString(), unit: "人日" };
    case "件":   return { value: Math.round(value).toLocaleString(), unit: "件" };
    default:     return { value: Math.round(value).toLocaleString(), unit };
  }
}

/** カード(入院KPI/入院実績)専用: 入院収入・粗利(GP) は 千円データ を 円 単位(億円/百万円に自動スケール)で表示。
 *  下の表 (AllMetricsTable/DeptMetricTable) は spec.unit=千円 のまま (スペース節約、 ユーザ指示 2026-06-24)。
 *  それ以外の指標・単位は fmtByUnitMain と同じ。 */
const YEN_CARD_METRICS = new Set(["入院収入", "GP", "手術収入", "外来収入", "外来化学療法収入"]);
export function fmtCardMain(
  value: number | null | undefined, unit: string, metricKey?: string,
): { value: string; unit: string } {
  if (metricKey && YEN_CARD_METRICS.has(metricKey) && unit === "千円") {
    return fmtByUnitMain(value == null ? value : value * 1000, "円");
  }
  // 人単位 (新規入院数 / 常勤医師数 等) のカードは 整数なら ".0" を出さない (98.7 等の端数は残す)。
  if (unit === "人" && value != null && Number.isFinite(value)) {
    return { value: value.toLocaleString(undefined, { maximumFractionDigits: 1 }), unit: "人" };
  }
  return fmtByUnitMain(value, unit);
}

/** 入院収入/粗利カードか (円表示の対象か) を判定。 */
export function isYenCardMetric(metricKey: string | undefined, unit: string): boolean {
  return !!metricKey && YEN_CARD_METRICS.has(metricKey) && unit === "千円";
}

/** 期間内の最大絶対値(千円)から 億(true)/万(false) を決定。 カード・マトリックスで共通利用し
 *  「期間内 max で 億/万 を判定」 を揃える (2026-06-24)。 */
export function yenUseOku(maxAbsK: number): boolean {
  return maxAbsK * 1000 >= 1e8;
}

/** 千円値を 固定単位 (useOku=億円 / それ以外=万円) で表示。 単位は期間 max で外から決める。 */
export function fmtYenScaled(value: number | null | undefined, useOku: boolean): { value: string; unit: string } {
  const unit = useOku ? "億円" : "万円";
  if (value == null || !Number.isFinite(value)) return { value: "—", unit };
  const yen = value * 1000;
  return useOku
    ? { value: (yen / 1e8).toFixed(2), unit }
    : { value: Math.round(yen / 1e4).toLocaleString(), unit };
}

/** カードグラフ y軸 tick 用: 円 値を 固定単位(億/万) で短縮 (例: 1.41e9,true → "14.1億")。 */
export function fmtYenAxisTick(yen: number, useOku: boolean): string {
  return useOku ? (yen / 1e8).toFixed(1) + "億" : Math.round(yen / 1e4).toLocaleString() + "万";
}

// ISO 8601 (UTC) 形式の日時文字列を JST に変換 ("2026-05-22 14:30" 形式)
export function fmtJst(iso: string | null | undefined): string {
  if (!iso || iso === "unknown") return iso ?? "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("ja-JP", {
      timeZone: "Asia/Tokyo",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
      hour12: false,
    });
  } catch {
    return iso;
  }
}
